/**
 * Toolbox Talk wizard — per-step Zod schemas.
 *
 * Lifted verbatim from
 * `apps/mobile/screens/wizards/WizardToolboxTalkScreen.tsx`
 * (Phase 15c).
 *
 * Step order:
 *
 *   1. topic     — library OR custom topic (mode-switched; `refine`d)
 *   2. body      — talk body (min 20 chars)
 *   3. keyPoints — at least one key point
 *   4. questions — optional list (zero is fine)
 *   5. attendees — at least one worker / subcontractor
 *   6. delivery  — date + delivered-by + duration
 *   7. preview   — read-only summary (no validation)
 *
 * NOTE: the topic step uses `z.object(...).refine(...)` so the
 * resulting schema is a `ZodEffects`, not a `ZodObject`. The composite
 * `ToolboxTalkWizardFullSchema` therefore composes shapes rather than
 * trying to extend the refined topic schema directly; the topic-level
 * `refine` is reapplied on the composite so the cross-field rule still
 * fires when validating an end-of-wizard payload.
 */
import { z } from "zod";

// ─── Shared sub-schemas ────────────────────────────────────────────────────

export const ToolboxTalkTopicModeSchema = z.enum(["library", "custom"]);
export type ToolboxTalkTopicMode = z.infer<typeof ToolboxTalkTopicModeSchema>;

export const ToolboxAttendeeKindSchema = z.enum(["worker", "subcontractor"]);
export type ToolboxAttendeeKind = z.infer<typeof ToolboxAttendeeKindSchema>;

export const ToolboxAttendeeSchema = z.object({
  id: z.string(),
  kind: ToolboxAttendeeKindSchema,
  name: z.string().min(1),
  company: z.string().nullable().optional(),
});
export type ToolboxAttendee = z.infer<typeof ToolboxAttendeeSchema>;

// ─── Per-step schemas ──────────────────────────────────────────────────────

/**
 * Topic step — picks the topic from the library OR enters a custom one.
 * Mirror the screen's `.refine(...)` with the same `path` + `message`
 * so the error surfaces against the same field id the mobile UI binds
 * to.
 */
const toolboxTalkTopicBase = z.object({
  topicMode: ToolboxTalkTopicModeSchema,
  topicName: z.string().optional().default(""),
  customTopicName: z.string().optional().default(""),
  customTopicDescription: z.string().optional().default(""),
});

export const ToolboxTalkTopicStepSchema = toolboxTalkTopicBase.refine(
  (v) =>
    v.topicMode === "library"
      ? v.topicName.trim().length > 0
      : v.customTopicName.trim().length > 0,
  {
    message: "Pick a topic or enter a custom one",
    path: ["topicName"],
  },
);
export type ToolboxTalkTopicStepData = z.infer<
  typeof ToolboxTalkTopicStepSchema
>;

export const ToolboxTalkBodyStepSchema = z.object({
  talkBody: z.string().min(20, "Talk body should be at least 20 characters"),
});
export type ToolboxTalkBodyStepData = z.infer<typeof ToolboxTalkBodyStepSchema>;

export const ToolboxTalkKeyPointsStepSchema = z.object({
  keyPoints: z.array(z.string().min(1)).min(1, "Add at least one key point"),
});
export type ToolboxTalkKeyPointsStepData = z.infer<
  typeof ToolboxTalkKeyPointsStepSchema
>;

/** Questions are optional — the schema allows empty. */
export const ToolboxTalkQuestionsStepSchema = z.object({
  questions: z.array(z.string()).optional().default([]),
});
export type ToolboxTalkQuestionsStepData = z.infer<
  typeof ToolboxTalkQuestionsStepSchema
>;

export const ToolboxTalkAttendeesStepSchema = z.object({
  attendees: z
    .array(ToolboxAttendeeSchema)
    .min(1, "Select at least one attendee"),
});
export type ToolboxTalkAttendeesStepData = z.infer<
  typeof ToolboxTalkAttendeesStepSchema
>;

export const ToolboxTalkDeliveryStepSchema = z.object({
  deliveryDate: z.string().min(1, "Delivery date is required"),
  deliveredBy: z.string().min(2, "Who's delivering this talk?"),
  duration: z.string().optional().default("10 minutes"),
});
export type ToolboxTalkDeliveryStepData = z.infer<
  typeof ToolboxTalkDeliveryStepSchema
>;

/** Preview is read-only — no validation. */
export const ToolboxTalkPreviewStepSchema = z.object({});
export type ToolboxTalkPreviewStepData = z.infer<
  typeof ToolboxTalkPreviewStepSchema
>;

// ─── Step ordering + composite ─────────────────────────────────────────────

export const TOOLBOX_TALK_WIZARD_STEPS = [
  { id: "topic", schema: ToolboxTalkTopicStepSchema },
  { id: "body", schema: ToolboxTalkBodyStepSchema },
  { id: "keyPoints", schema: ToolboxTalkKeyPointsStepSchema },
  { id: "questions", schema: ToolboxTalkQuestionsStepSchema },
  { id: "attendees", schema: ToolboxTalkAttendeesStepSchema },
  { id: "delivery", schema: ToolboxTalkDeliveryStepSchema },
  { id: "preview", schema: ToolboxTalkPreviewStepSchema },
] as const;

/**
 * Composite — re-applies the topic refine since topic is a
 * `ZodEffects` wrapping a plain object; we need the cross-field rule on
 * the full payload too.
 */
export const ToolboxTalkWizardFullSchema = z
  .object({
    ...toolboxTalkTopicBase.shape,
    ...ToolboxTalkBodyStepSchema.shape,
    ...ToolboxTalkKeyPointsStepSchema.shape,
    ...ToolboxTalkQuestionsStepSchema.shape,
    ...ToolboxTalkAttendeesStepSchema.shape,
    ...ToolboxTalkDeliveryStepSchema.shape,
  })
  .refine(
    (v) =>
      v.topicMode === "library"
        ? v.topicName.trim().length > 0
        : v.customTopicName.trim().length > 0,
    {
      message: "Pick a topic or enter a custom one",
      path: ["topicName"],
    },
  );
export type ToolboxTalkWizardData = z.infer<typeof ToolboxTalkWizardFullSchema>;
